import { MouseEvent, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { handleError } from "../common/utils";
import { useMatterContext } from "../contexts/MatterContext";
import { useDeficiencyReviewService } from "../services/deficiency-review.service";
import { useDeficiencyService } from "../services/deficiency.service";
import { useInvalidSheetService } from "../services/invalid-sheet.service";
import { useMatterService } from "../services/matter.service";
import { useWorkService } from "../services/work.service";
import CircleCounterIcon from "../components/circle-counter-icon";
import ConfirmModal from "../components/confirm-modal";
interface MatterDetailsActionsProps {
  ruleRunCompletionTimeStamp?: Date;
  onRunSelectRules?: () => void;
}

export default function MatterDetailsActions({
  ruleRunCompletionTimeStamp,
  onRunSelectRules,
}: MatterDetailsActionsProps) {
  const navigate = useNavigate();
  const { matterId } = useParams();
  const { getValidSignaturesCsv, closeMatter, reopenMatter } = useMatterService();
  const { matter, refreshMatter } = useMatterContext();
  const { getAllSignaturesCsv } = useDeficiencyService();
  const { getReviewableDeficiencyCountByMatter } = useDeficiencyReviewService();
  const { getFlaggedWorkCount, autoCompleteDates } = useWorkService();
  const { getInvalidSheetCountByMatter } = useInvalidSheetService();

  const [errorExport, setErrorExport] = useState("");
  const [deficiencyReviewCount, setDeficiencyReviewCount] = useState(0);
  const [transcriptionReviewCount, setTranscriptionReviewCount] = useState(0);
  const [invalidSheetCount, setInvalidSheetCount] = useState(0);
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [showReopenModal, setShowReopenModal] = useState(false);

  const csvLink = useRef<HTMLAnchorElement>(null);

  useEffect(() => {
    if (matterId) {
      getFlaggedWorkCount(matterId).then((flaggedWorkCount) => {
        setTranscriptionReviewCount(flaggedWorkCount);
      });
    }
  }, [getFlaggedWorkCount, matterId]);

  useEffect(() => {
    if (matterId) {
      getReviewableDeficiencyCountByMatter(matterId).then((count) => {
        setDeficiencyReviewCount(count);
      });
    }
  }, [getReviewableDeficiencyCountByMatter, matterId, ruleRunCompletionTimeStamp]);

  useEffect(() => {
    if (matterId) {
      getInvalidSheetCountByMatter(matterId).then((count) => {
        setInvalidSheetCount(count);
      });
    }
  }, [getInvalidSheetCountByMatter, matterId]);

  const exportValidSignatures = () => {
    setErrorExport("");
    getValidSignaturesCsv(matterId!).then(
      (csv) => {
        csvLink.current?.setAttribute("download", `Matter${matterId}ValidSignatures.csv`);
        csvLink.current?.setAttribute("href", URL.createObjectURL(new Blob([csv], { type: "text/csv" })));
        csvLink.current?.click();
      },
      (error) => {
        handleError(error, setErrorExport);
      }
    );
  };

  const exportAllSignaturesCsv = () => {
    setErrorExport("");
    getAllSignaturesCsv(matterId!).then(
      (csv) => {
        csvLink.current?.setAttribute("download", `Matter${matterId}AllSignatures.csv`);
        csvLink.current?.setAttribute("href", URL.createObjectURL(new Blob([csv], { type: "text/csv" })));
        csvLink.current?.click();
      },
      (error) => {
        handleError(error, setErrorExport);
      }
    );
  };

  function handleNavigateToDeficiencyReview(e: MouseEvent<HTMLAnchorElement, globalThis.MouseEvent>): void {
    e.preventDefault();
    navigate(`/admin/matters/${matterId}/deficiencies/review`, {
      state: { from: window.location.pathname },
    });
  }

  const handleAutoCompleteDates = () => {
    setErrorExport("");
    if (matterId) {
      autoCompleteDates(matterId);
    }
  };

  const handleCloseMatter = () => {
    if (matterId) {
      closeMatter(matterId).then(
        () => { refreshMatter(); },
        (error) => { handleError(error, setErrorExport); }
      );
    }
  };

  const handleReopenMatter = () => {
    if (matterId) {
      reopenMatter(matterId).then(
        () => { refreshMatter(); },
        (error) => { handleError(error, setErrorExport); }
      );
    }
  };

  return (
    <div className="d-flex flex-row gap-2">
      <Link
        to={deficiencyReviewCount > 0 ? `/admin/matters/${matterId}/deficiencies/review` : "#"}
        className={`btn btn-sw btn-sm btn-outline${deficiencyReviewCount <= 0 ? " disabled" : ""}`}
        aria-disabled={deficiencyReviewCount <= 0}
        onClick={(e) => {
          if (deficiencyReviewCount <= 0) {
            e.preventDefault();
            return;
          }
          handleNavigateToDeficiencyReview(e);
        }}
      >
        <div className="ps-2">
          <CircleCounterIcon count={deficiencyReviewCount} />
          Deficiency Review
        </div>
      </Link>

      <Link
        to={`/admin/matters/${matterId}/sheets/invalid`}
        className={`btn btn-sw btn-sm btn-outline${invalidSheetCount <= 0 ? " disabled" : ""}`}
        aria-disabled={invalidSheetCount <= 0}
      >
        <div className="ps-2">
          <CircleCounterIcon count={invalidSheetCount} />
          Invalid Sheet Review
        </div>
      </Link>

      <Link
        to={`/admin/matters/${matterId}/review/transcription`}
        className={`btn btn-sw btn-sm btn-outline${transcriptionReviewCount <= 0 ? " disabled" : ""}`}
        aria-disabled={transcriptionReviewCount <= 0}
      >
        <div className="ps-2">
          <CircleCounterIcon count={transcriptionReviewCount} />
          Transcription Review
        </div>
      </Link>

      <div className="ms-auto" style={{ marginRight: "-12px" }}>
        <div className="dropdown">
          <button
            className="btn btn-sw btn-sm btn-outline dropdown-toggle"
            type="button"
            id="dropdownMenuButton"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
          >
            Actions
          </button>
          <div className="dropdown-menu" aria-labelledby="dropdownMenuButton">
            <a ref={csvLink} style={{ display: "none" }} target="_blank"></a>

            <Link to={`/admin/matters/${matterId}/deficiencies`} className="dropdown-item">
              Deficiencies
            </Link>

            <button onClick={exportValidSignatures} className="dropdown-item">
              Export Valid
            </button>

            <button onClick={exportAllSignaturesCsv} className="dropdown-item">
              Export All
            </button>

            <Link to={`/admin/matters/${matterId}/sheets`} className="dropdown-item">
              Signature Sheets
            </Link>

            <Link to={`/admin/matters/${matterId}/upload-invalid`} className="dropdown-item">
              Upload Invalid
            </Link>

            <Link to={`/admin/matters/${matterId}/sheet-works`} className="dropdown-item">
              Work by Sheet
            </Link>
            <Link to={`/admin/matters/${matterId}/free-form-voter-search`} className="dropdown-item">
              Voter Search
            </Link>
            <Link to={`/admin/matters/${matterId}/free-form-circulator-search`} className="dropdown-item">
              Circulator Search
            </Link>
            <div className="dropdown-divider"></div>
            <Link to={`/admin/matters/${matterId}/deficiency-queue`} className="dropdown-item">
              Deficiency Review Queue
            </Link>
            <Link to={`/admin/matters/${matterId}/unreleased`} className="dropdown-item">
              Unreleased Sheet Queue
            </Link>
            <Link to={`/admin/matters/${matterId}/transcription-review-queue`} className="dropdown-item">
              Transcription Review Queue
            </Link>
            <Link to={`/admin/matters/${matterId}/transcription-queue`} className="dropdown-item">
              Transcription Queue
            </Link>
            <div className="dropdown-divider"></div>

            <button onClick={onRunSelectRules} className="dropdown-item">
              Run Select Rules
            </button>

            <button onClick={handleAutoCompleteDates} className="dropdown-item">
              Auto-Complete Dates
            </button>

            <div className="dropdown-divider"></div>

            {matter && !matter.isClosed && (
              <button onClick={() => setShowCloseModal(true)} className="dropdown-item">
                Close Matter
              </button>
            )}
            {matter && matter.isClosed && (
              <button onClick={() => setShowReopenModal(true)} className="dropdown-item">
                Reopen Matter
              </button>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showCloseModal}
        title="Close Matter"
        message="Are you sure you want to close this matter? All reviewers assigned to this matter will be locked."
        action={handleCloseMatter}
        close={() => setShowCloseModal(false)}
      />
      <ConfirmModal
        isOpen={showReopenModal}
        title="Reopen Matter"
        message="Are you sure you want to reopen this matter? All reviewers assigned to this matter will be unlocked."
        action={handleReopenMatter}
        close={() => setShowReopenModal(false)}
      />
    </div>
  );
}
